/* eslint-disable no-nested-ternary */
import {
  Input,
  Tag
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  Controller, FormProvider,
  UseFormReturn,
  useWatch
} from 'react-hook-form';
import { Label, Radio, Typography } from 'tfc-components';

import {
  InvoiceFormType
} from '.';

import InputCustom from 'components/atoms/InputCustom';
import FormField from 'components/organisms/FormField';

interface BillingTypeProps {
  triggerSubmitRef?: React.MutableRefObject<(() => void) | null>;
  initialData?: InvoiceFormType;
  method: UseFormReturn<InvoiceFormType>
}

const initialInvoiceEmail = (value: string[] | undefined): string => (value ? value.map((email) => email.trim()).join(',') : '');
const BillingInformation: React.FC<BillingTypeProps> = ({
  initialData,
  method
}) => {
  const [inputValue, setInputValue] = useState<string>(
    initialInvoiceEmail(initialData?.billingInfo?.invoiceEmail)
  );
  const [listOption, setListOption] = useState<string[]>([]);

  const invoiceType = useWatch({
    control: method.control,
    name: 'invoiceType'
  });

  const invoiceEmailError = method.formState.errors?.billingInfo?.invoiceEmail;

  useEffect(() => {
    if (initialData?.billingInfo?.invoiceEmails) {
      setInputValue(initialData.billingInfo?.invoiceEmails.toString());
      method.setValue('billingInfo.invoiceEmail', initialData.billingInfo?.invoiceEmails, { shouldDirty: true, shouldValidate: true });
      const newOptions = initialData.billingInfo?.invoiceEmails.toString()
        .split(',')
        .map((opt) => opt.trim())
        .filter((opt) => opt && !listOption.includes(opt));
      setListOption((prev) => [...prev, ...newOptions]);
      setInputValue('');
    }
  }, [initialData, method]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && inputValue.trim() && !invoiceEmailError) {
      const newOptions = inputValue
        .split(',')
        .map((opt) => opt.trim())
        .filter((opt) => opt && !listOption.includes(opt));

      setListOption((prev) => [...prev, ...newOptions]);
      setInputValue('');
    }
  };

  const handleRemove = (removedOption: string) => {
    setListOption((prev) => prev.filter((opt) => opt !== removedOption));
  };

  return (
    <div className="p-checkout_billingInfo">
      <FormProvider {...method}>
        <div className="p-checkout_billingType">
          <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct p-checkout_billingInfo_spacing fs-20x24">Invoice Information</Typography.Text>
          <Label className="p-checkout_billingType_option">
            <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
              Invoice Recipient Type
              <span className="p-home_demo_quotes">*</span>
            </Typography.Text>
          </Label>
          <Controller
            name="invoiceType"
            render={({ field }) => (
              <div className="p-checkout_billingType_options">
                <Label className="p-checkout_billingType_option">
                  <Radio
                    size={20}
                    checked={field.value === 2}
                    onChange={() => field.onChange(2)}
                    name="invoiceType"
                  />
                  <Typography.Text extendClasses="p-checkout_billingType_optionText">
                    Business
                  </Typography.Text>
                </Label>
                <Label className="p-checkout_billingType_option">
                  <Radio
                    size={20}
                    name="invoiceType"
                    checked={field.value === 1}
                    onChange={() => field.onChange(1)}
                  />
                  <Typography.Text extendClasses="p-checkout_billingType_optionText">Individual</Typography.Text>
                </Label>
              </div>
            )}
          />
        </div>
        {invoiceType === 2 ? (
          <>
            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
                <Controller
                  name="billingInfo.taxId"
                  control={method.control}
                  render={({ field, fieldState }) => (
                    <FormField label="Tax ID" required>
                      <InputCustom
                        {...field}
                        className="p-checkout_billingInfo_input p-home_demo_formInput"
                        placeholder="0312345678"
                        errText={fieldState.error?.message}
                      />
                    </FormField>
                  )}
                />
              </div>

              <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
                <Controller
                  name="billingInfo.companyName"
                  control={method.control}
                  render={({ field, fieldState }) => (
                    <FormField label="Company Name" required>
                      <InputCustom
                        {...field}
                        className="p-checkout_billingInfo_input p-home_demo_formInput"
                        placeholder="Company ABC"
                        errText={fieldState.error?.message}
                      />
                    </FormField>
                  )}
                />
              </div>
            </div>

            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Controller
                  name="billingInfo.fullAddress"
                  control={method.control}
                  render={({ field, fieldState }) => (
                    <FormField icon="question" label="Full Address" required tooltipContent="Please enter the registered address as provided to the tax authority.">
                      <InputCustom
                        {...field}
                        placeholder="123 Nguyen Hue St, Ben Nghe Ward, District 1, HCMC"
                        className="p-checkout_billingInfo_input p-home_demo_formInput"
                        errText={fieldState.error?.message}
                      />

                    </FormField>
                  )}
                />
              </div>
            </div>

            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Invoice Email
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="billingInfo.invoiceEmail"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <div className="p-changePlan_inputWrap">
                        <div className="p-addNewPackage_listTag">
                          {listOption.map((option, index) => (
                            <>
                              {index > 0
                                && (
                                  <Typography.Text
                                    extendClasses="p-addNewPackage_mr10"
                                  >
                                    ,
                                  </Typography.Text>
                                )}
                              <Tag
                                key={option}
                                closable
                                onClose={() => handleRemove(option)}
                                className="p-addNewPackage_tag"
                              >
                                {option}
                              </Tag>
                            </>
                          ))}
                        </div>
                        <Input
                          placeholder={listOption.length === 0 ? 'email1@example.com, email2@example.com' : ''}
                          className="modalOption_textInput"
                          onKeyDown={handleKeyDown}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (value !== ' ') {
                              setInputValue(value);
                              field.onChange(listOption.concat(value));
                            }
                          }}
                          value={inputValue}
                          style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
                        />

                      </div>
                      {invoiceEmailError && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                          {Array.isArray(invoiceEmailError)
                            ? (invoiceEmailError[0] && typeof
                              invoiceEmailError[0] === 'object' && 'message' in invoiceEmailError[0])
                              ? invoiceEmailError[0].message
                              : 'Invalid email'
                            : (invoiceEmailError as any)?.message || 'Invalid email'}
                        </Typography.Text>
                      )}
                    </>

                  )}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  National ID Number
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="billingInfo.nationalId"
                  control={method.control}
                  render={({ field, fieldState }) => (
                    <InputCustom
                      {...field}
                      className="p-checkout_billingInfo_input p-home_demo_formInput"
                      placeholder="23456789012"
                      errText={fieldState.error?.message}
                    />
                  )}
                />
              </div>
              <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Full Name
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="billingInfo.fullName"
                  control={method.control}
                  render={({ field, fieldState }) => (
                    <InputCustom
                      {...field}
                      className="p-checkout_billingInfo_input p-home_demo_formInput"
                      placeholder="Nguyen Van A"
                      errText={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Full Address
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="billingInfo.fullAddress"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        placeholder="123 Nguyen Hue St, Ben Nghe Ward, District 1, HCMC"
                        className="p-checkout_billingInfo_input p-home_demo_formInput"
                      />
                      {(method.formState.errors as any).invoice?.fullAddress && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                          {(method.formState.errors as any).invoice?.fullAddress.message}
                        </Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Invoice Email
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="billingInfo.invoiceEmail"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <div className="p-changePlan_inputWrap">
                        <div className="p-addNewPackage_listTag">
                          {listOption.map((option, index) => (
                            <>
                              {index > 0
                                && (
                                  <Typography.Text
                                    extendClasses="p-addNewPackage_mr10"
                                  >
                                    ,
                                  </Typography.Text>
                                )}
                              <Tag
                                key={option}
                                closable
                                onClose={() => handleRemove(option)}
                                className="p-addNewPackage_tag"
                              >
                                {option}
                              </Tag>
                            </>
                          ))}
                        </div>
                        <Input
                          placeholder={listOption.length === 0 ? 'email1@example.com, email2@example.com' : ''}
                          className="modalOption_textInput"
                          onKeyDown={handleKeyDown}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (value !== ' ') {
                              setInputValue(value);
                              field.onChange(listOption.concat(value));
                            }
                          }}
                          value={inputValue}
                          style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
                        />

                      </div>
                      {invoiceEmailError && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                          {Array.isArray(invoiceEmailError)
                            ? (invoiceEmailError[0] && typeof
                              invoiceEmailError[0] === 'object' && 'message' in invoiceEmailError[0])
                              ? invoiceEmailError[0].message
                              : 'Invalid email'
                            : (invoiceEmailError as any)?.message || 'Invalid email'}
                        </Typography.Text>
                      )}
                    </>

                  )}
                />
              </div>
            </div>
          </>
        )}
        <button type="submit" style={{ display: 'none' }} />
      </FormProvider>
    </div>
  );
};

export default BillingInformation;
